import React, { useState } from 'react';
import Layout from '../../components/Layout';
import DriverSearch from '../../components/DriverSearch';
import driverAPI from '../../api/driverAPI';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { dateStyle: 'medium' }) : '—';

const today = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const MONTH_NAMES = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];

export default function DriverPayoutReportPage() {
  const [driver, setDriver]   = useState(null);
  const [from, setFrom]       = useState(firstOfMonth());
  const [to, setTo]           = useState(today());
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // Editable footer fields (not persisted on waybill)
  const [gateSop, setGateSop]           = useState('');
  const [allowance, setAllowance]       = useState('');
  const [ootAllowance, setOotAllowance] = useState('');

  const handleRun = async () => {
    if (!driver) { setError('Please select a driver.'); return; }
    if (!from || !to) { setError('Please select a date range.'); return; }
    setError('');
    setLoading(true);
    setReport(null);
    try {
      const res = await driverAPI.getPayoutSummary(driver._id, from, to);
      setReport(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!driver || !report) return;
    const params = new URLSearchParams({
      driverId:  driver._id,
      from,
      to,
      gateSop:   gateSopNum,
      allowance: allowanceNum,
      oot:       ootNum,
    });
    window.open(`/billing/reports/print/driver-payout?${params}`, '_blank');
  };

  const d = report?.driver;
  const reportHeader = d
    ? `Weekly Report — ${d.nickname || d.name}${d.truckColor ? ` - ${d.truckColor}` : ''}${d.truckPlateCode ? ` ${d.truckPlateCode}` : ''} (${fmtDate(report.period.from)} – ${fmtDate(report.period.to)})`
    : '';

  const gateSopNum    = parseFloat(gateSop)    || 0;
  const allowanceNum  = parseFloat(allowance)  || 0;
  const ootNum        = parseFloat(ootAllowance) || 0;
  const additions     = gateSopNum + allowanceNum + ootNum;

  const totalRate     = report?.waybills?.reduce((s, w) => s + (w.truckingRate || 0) * (d?.payoutRate ?? 0), 0) || 0;
  const grandTotal         = report?.totalAmount || 0;
  const totalTruckingAmount = report?.totalTruckingAmount || 0;
  const driverPayout  = report
    ? (totalTruckingAmount * (d?.payoutRate ?? 0.10))
    : 0;
  const netPayout     = driverPayout + additions;

  const handleExportCSV = () => {
    if (!report || !d) return;

    const fromDate = new Date(report.period.from);
    const toDate   = new Date(report.period.to);
    const monthName  = MONTH_NAMES[fromDate.getMonth()];
    const periodStr  = `${monthName} ${fromDate.getDate()}-${toDate.getDate()},${toDate.getFullYear()}`;
    const title      = `Weekly Report - ${d.nickname || d.name}${d.truckColor ? ` - ${d.truckColor}` : ''}${d.truckPlateCode ? ` ${d.truckPlateCode}` : ''}(${periodStr})`;

    const fmtNum = (n) => (n || 0).toFixed(2);
    const fmtDateCSV = (dt) => {
      if (!dt) return '';
      const date = new Date(dt);
      return `${String(date.getMonth() + 1).padStart(2, '0')} ${String(date.getDate()).padStart(2, '0')} ${date.getFullYear()}`;
    };

    const headers  = ['RATE', 'WAYBILL NO.', 'DATE', 'CONSIGNEE', 'DELIVERY ADD', 'VAN NO.', 'VAN SIZE', 'TRUCKING RATE', 'STRIPPING FEE', 'TOTAL'];
    const dataRows = report.waybills.map((wb) => [
      fmtNum(wb.truckingRate * (d?.payoutRate ?? 0)),
      wb.waybillNo  || '',
      fmtDateCSV(wb.date),
      wb.consignee  || '',
      wb.deliveryAdd || '',
      wb.vanNo      || '',
      wb.vanSize    || '',
      fmtNum(wb.truckingRate),
      fmtNum(wb.strippingFee),
      fmtNum(wb.total),
    ]);

    const vanSizeTotal = report.waybills.reduce((s, wb) => s + (parseFloat(wb.vanSize) || 0), 0);
    const empty10 = Array(10).fill('');

    const vanSizeRow = [...empty10];
    vanSizeRow[6] = fmtNum(vanSizeTotal);

    const totalRow = [...empty10];
    totalRow[0] = d.nickname || d.name;
    totalRow[3] = 'TOTAL';
    totalRow[7] = 'Php';
    totalRow[8] = fmtNum(totalRate);
    totalRow[9] = fmtNum(grandTotal);

    const payoutRow = [...empty10];
    payoutRow[3] = `DRIVER PAYOUT (${(d.payoutRate * 100).toFixed(0)}%)`;
    payoutRow[9] = fmtNum(driverPayout);

    const additionRows = [
      ['GATE SOP',         gateSopNum],
      ['ALLOWANCE',        allowanceNum],
      ['ADD OOT ALLOWANCE', ootNum],
    ].map(([label, val]) => {
      const r = [...empty10];
      r[3] = label;
      r[8] = fmtNum(val);
      return r;
    });

    const netPayoutRow = [...empty10];
    netPayoutRow[3] = 'NET PAYOUT';
    netPayoutRow[9] = fmtNum(netPayout);

    const allRows = [
      [title, ...Array(9).fill('')],
      empty10,
      headers,
      ...dataRows,
      empty10,
      vanSizeRow,
      empty10,
      ...additionRows,
      totalRow,
      payoutRow,
      ...(additions > 0 ? [netPayoutRow] : []),
    ];

    const csv = allRows
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const fmt8 = (dt) => {
      const d2 = new Date(dt);
      return `${d2.getFullYear()}${String(d2.getMonth() + 1).padStart(2, '0')}${String(d2.getDate()).padStart(2, '0')}`;
    };
    const nick  = (d.nickname || d.name).replace(/\s+/g, '');
    const truck = `${(d.truckColor || '').replace(/\s+/g, '')}${(d.truckPlateCode || '').replace(/\s+/g, '')}`;
    const filename = `WeeklyReport_${nick}_${truck}_${fmt8(report.period.from)}-${fmt8(report.period.to)}.csv`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout pageTitle="Driver Payout Report">
      <div className="space-y-4 max-w-5xl">
        {/* ── Filters ── */}
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Report Parameters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Driver</label>
              <DriverSearch value={driver} onChange={setDriver} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
              <input
                type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
              <input
                type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button
              onClick={handleRun}
              disabled={loading}
              className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Loading…' : 'Run Report'}
            </button>
            {report && report.waybillCount > 0 && (
              <>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                >
                  Print
                </button>
                <button
                  onClick={handleExportCSV}
                  className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium hover:bg-green-100"
                >
                  Export CSV
                </button>
              </>
            )}
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>
          )}
        </div>

        {/* ── Report output ── */}
        {report && (
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            {/* Report Header */}
            <div className="text-center space-y-0.5">
              <h1 className="text-base font-bold text-gray-900 uppercase tracking-wide">
                {reportHeader}
              </h1>
              <p className="text-xs text-gray-500">
                {report.waybillCount} waybill{report.waybillCount !== 1 ? 's' : ''} &nbsp;·&nbsp; Payout Rate: {(d.payoutRate * 100).toFixed(0)}%
              </p>
            </div>

            {/* Waybill Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['RATE', 'WAYBILL NO.', 'DATE', 'CONSIGNEE', 'DELIVERY ADD', 'VAN NO.', 'VAN SIZE', 'TRUCKING RATE', 'STRIPPING FEE', 'TOTAL'].map(
                      (h) => (
                        <th key={h} className="px-2 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 whitespace-nowrap">
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.waybills.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-2 py-6 text-center text-gray-400">No waybills found for this period.</td>
                    </tr>
                  ) : (
                    report.waybills.map((wb, i) => (
                      <tr key={wb.waybillId} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-right">{PHP(wb.truckingRate * (d?.payoutRate ?? 0))}</td>
                        <td className="px-2 py-1.5 font-medium">{wb.waybillNo}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(wb.date)}</td>
                        <td className="px-2 py-1.5">{wb.consignee}</td>
                        <td className="px-2 py-1.5">{wb.deliveryAdd}</td>
                        <td className="px-2 py-1.5">{wb.vanNo || '—'}</td>
                        <td className="px-2 py-1.5">{wb.vanSize || '—'}</td>
                        <td className="px-2 py-1.5 text-right">{PHP(wb.truckingRate)}</td>
                        <td className="px-2 py-1.5 text-right">{PHP(wb.strippingFee)}</td>
                        <td className="px-2 py-1.5 text-right font-medium">{PHP(wb.total)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer additions + payout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
              {/* Editable additions */}
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Additions / Allowances</h3>
                <EditableFooterRow label="GATE SOP" value={gateSop} onChange={setGateSop} />
                <EditableFooterRow label="ALLOWANCE" value={allowance} onChange={setAllowance} />
                <EditableFooterRow label="ADD OOT ALLOWANCE" value={ootAllowance} onChange={setOotAllowance} />
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
                <SummaryRow label={`TOTAL (RATE)`} value={PHP(totalRate)} />
                <SummaryRow label="GRAND TOTAL" value={PHP(grandTotal)} />
                {additions > 0 && (
                  <SummaryRow label="TOTAL ADDITIONS" value={PHP(additions)} />
                )}
                <div className="border-t border-gray-200 pt-2 mt-1">
                  <SummaryRow label={`DRIVER PAYOUT (${(d.payoutRate * 100).toFixed(0)}%)`} value={PHP(driverPayout)} bold />
                  {additions > 0 && (
                    <SummaryRow label="NET PAYOUT" value={PHP(netPayout)} bold />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function EditableFooterRow({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-xs font-medium text-gray-600 w-40 shrink-0">{label}</label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0.00"
        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 w-36 text-right"
      />
    </div>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
      <span>{label}</span>
      <span className="font-mono">₱{value}</span>
    </div>
  );
}
